// https://prototype.sqprod.co/#/docs/squareup.franklin.api.FormBlocker.Element.ButtonElement
import React, { MutableRefObject, useContext, useState } from 'react'

import { useFormContext } from 'react-hook-form'

import { Button } from '@cash-design-system/react'
import { BlockerAction } from '@cash-web/protos/squareup/franklin/blocker_action.pb'
import {
  FormBlockerElementButtonElement,
  FormBlockerElementButtonElementStyle,
  FormBlockerElementButtonElementType,
} from '@cash-web/protos/squareup/franklin/form_blocker.pb'
import { ErrorContext } from '@cash-web/shared-data-access-error'
import { useLogCashWebCdfEvent } from '@cash-web/shared-feature-analytics'
import { WebViewUnsupportedFeature } from '@squareup/customer-data-events-web'

import { getBlockerAction } from '../../../FlowProvider/hooks/useBlockerAction'
import { FormBlockerContext } from '../../FormBlockerContext'

export type ButtonVariants = React.ComponentProps<typeof Button>['variant']
type SpaceButtonProps = { space?: string }
type Props = FormBlockerElementButtonElement & {
  disabled?: boolean
  canSubmit?: boolean
  selectedAction?: MutableRefObject<string | undefined>
} & SpaceButtonProps

const getVariant = (
  action: BlockerAction,
  style?: FormBlockerElementButtonElementStyle,
  type?: FormBlockerElementButtonElementType
): ButtonVariants => {
  if (action.view_action?.title === 'direct-deposit-authorization-form') {
    return 'primary'
  } else if (type === FormBlockerElementButtonElementType.DESTRUCTIVE) {
    return 'error'
  } else if (style === FormBlockerElementButtonElementStyle.PRIMARY) {
    return 'primary'
  } else if (style === FormBlockerElementButtonElementStyle.SECONDARY) {
    return 'secondary'
  } else if (style === FormBlockerElementButtonElementStyle.TERTIARY) {
    return 'tertiary'
  }

  return 'primary'
}

export function ButtonElement({
  action,
  style,
  type,
  disabled,
  canSubmit,
  selectedAction,
}: Props): React.ReactElement | null {
  const context = useContext(FormBlockerContext)
  const { error } = useContext(ErrorContext)
  const form = useFormContext()
  const logCdfEvent = useLogCashWebCdfEvent()

  const [isNonPrimaryOrSecondaryButtonLoading, setIsNonPrimaryOrSecondaryButtonLoading] = useState<boolean>(false)

  if (!action) {
    return null
  }

  const variant = getVariant(action, style, type)

  const isLoading = canSubmit
    ? form?.formState?.isSubmitting && selectedAction?.current === variant
    : isNonPrimaryOrSecondaryButtonLoading && !error
  const blockerAction = getBlockerAction(action)
  if (!blockerAction.isSupported) {
    logCdfEvent(
      WebViewUnsupportedFeature({
        source: 'ButtonElement',
        feature: 'BLOCKER_ACTION',
        action: blockerAction.actionName,
      })
    )
    return null
  }

  return (
    <Button
      size="large"
      role="button"
      type={canSubmit ? 'submit' : 'button'}
      variant={variant}
      disabled={disabled}
      loading={isLoading}
      onClick={() => {
        if (selectedAction) {
          selectedAction.current =
            type === FormBlockerElementButtonElementType.DESTRUCTIVE &&
            style === FormBlockerElementButtonElementStyle.PRIMARY
              ? 'primary'
              : variant
        }
        if (!canSubmit) {
          if (action.submit_action) {
            setIsNonPrimaryOrSecondaryButtonLoading(true)
          }
          context.onFormBlockerAction(action)
        }
      }}
    >
      {action.text}
    </Button>
  )
}
